import React, { useMemo } from 'react';
import { RouteKeys } from 'models/RouteModels';
import {
    containerStyles,
    degenRankings,
    filterAccionPredSectsByFullJoint,
    getThumbnailXray,
    getWorstCaseDegeneration,
    getXraysMappedToActiveJoints,
    isAllActiveJointXraySectionsConfirmed,
    mapClientFavoredPredictionsFromAccionPredSects,
} from 'helpers/helpers';
import MDTypography from 'components/atoms/MDTypography/MDTypography';
import MDBox from 'components/atoms/MDBox/MDBox';
import colors from 'assets/theme/base/colors';

import MDButton from 'components/atoms/MDButton/MDButton';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronLeft } from '@fortawesome/free-solid-svg-icons';
import { useBreakpoints } from 'hooks/useBreakpoints';
import { Breakpoints } from 'models/StyleModels';
import { FullJoints, Xray } from 'models/XrayModels';
import { Appointment } from 'models/AppointmentModels';
import JointOutcomesCardLoader from '../JointOutcomesCardLoader/JointOutcomesCardLoader';
import { RequiredViews } from 'hooks/useAppointmentJointViewsData';
import TriageJointOutcomesSection from '../TriageJointOutcomesSection/TriageJointOutcomesSection';
import { PRO } from 'models/SurveyModels';
import ActionSlugs from 'services/PermissionsService';
import { User } from 'models/UserModels';
import { Patient } from 'models/PatientModels';
import { useNavigate } from 'react-router-dom';
import { routesMap } from 'data/routes';
import { useNavigation } from 'hooks/useNavigation';
import Container from 'components/pages/ExamRoomAllJointsPage/ExamRoomAllJointPage.styles';

interface ExamRoomProps {
    user: User | null,
    appointment: Appointment | null,
    xraysMappedToJoints: {
        "left knee"?: Xray[] | undefined;
        "right knee"?: Xray[] | undefined;
        "left hip"?: Xray[] | undefined;
        "right hip"?: Xray[] | undefined;
    },
    latestProsMappedToJoints: {
        "left knee"?: PRO | undefined;
        "right knee"?: PRO | undefined;
        "left hip"?: PRO | undefined;
        "right hip"?: PRO | undefined;
    },
    roiImgUrls: {
        [key: string]: string;
    }
    appointmentRequiredJointViews: Record<FullJoints, RequiredViews> | undefined,
    batchConfirmXrays: (xrays: Xray[], fullJoint?: FullJoints) => Promise<void>,
    activePatient: Patient | null,
    flow: string | null,
    initalQueriesLoading: boolean,
    updateXrayLoading: boolean,
    roiImagesLoading: boolean,
}

export const ExamRoomContent: React.FC<ExamRoomProps> = (props: ExamRoomProps) => {

    const {
        user,
        appointment,
        xraysMappedToJoints,
        latestProsMappedToJoints,
        appointmentRequiredJointViews,
        roiImgUrls,
        activePatient,
        flow,
        batchConfirmXrays,
        initalQueriesLoading,
        roiImagesLoading,
        updateXrayLoading } = props;

    const { breakpointBreached: lgBreached } = useBreakpoints({ breakpoint: Breakpoints.LARGE });
    const { breakpointBreached: mdBreached } = useBreakpoints({ breakpoint: Breakpoints.MEDIUM });

    const { UPDATE$XRAY } = ActionSlugs;

    const { navigateToBaseRouteWithAptmtDetails } = useNavigation();
    const navigate = useNavigate();

    const xraysMappedToActiveJoints = useMemo(() => getXraysMappedToActiveJoints(appointment, xraysMappedToJoints), [appointment, xraysMappedToJoints]);

    const allActiveJointXRays = useMemo(() => {
        return Object.values(xraysMappedToActiveJoints).flat();
    }, [xraysMappedToActiveJoints]);
    const allXraysConfirmed = flow === "DEMO" ? true : isAllActiveJointXraySectionsConfirmed(appointment, allActiveJointXRays);

    let flowBackRoute = `${routesMap[RouteKeys.APPOINTMENT_OVERVIEW].baseRoute}/${appointment?.patient_id}?date=${appointment?.appointment_date}`
    let reportRoute = RouteKeys.REPORT

    if (flow === "TRIAGE") {
        flowBackRoute = routesMap[RouteKeys.TRIAGE_LIST].route
        reportRoute = RouteKeys.TRIAGE_REPORT
    } else if (flow === "DEMO") {
        reportRoute = RouteKeys.DEMO_REPORT
    }

    const sortKey: { [key in FullJoints]?: { worstCase: number, proScore: number, jointPriority: number | null } } =
        Object
            .entries(xraysMappedToActiveJoints)
            .filter(([_, xrays]) => xrays.some(xrays => xrays.prediction_results))
            .reduce((acc, [fullJoint, xRays]) => {
                return {
                    ...acc,
                    [fullJoint]: {
                        worstCase:
                            degenRankings[
                            getWorstCaseDegeneration(
                                mapClientFavoredPredictionsFromAccionPredSects(
                                    filterAccionPredSectsByFullJoint(
                                        xRays.map((xray) => xray.prediction_results.sections).flat() || [],
                                        fullJoint as FullJoints,
                                    )
                                ))!],
                        proScore: latestProsMappedToJoints[fullJoint as FullJoints]?.scores?.overall,
                        jointPriority: appointment?.joints?.[fullJoint as FullJoints] || null
                    }
                }
            }, {})

    const jointCards = (initalQueriesLoading && !!appointment) || !appointmentRequiredJointViews
        ? Object.entries(FullJoints).map(([fj]) => (
            <JointOutcomesCardLoader
                key={fj}
                displayVertical={mdBreached}
            />
        ))
        : Object.entries(sortKey || {})
            .sort(([, aSort], [, bSort]) => {
                if (flow === "CLASSIC") {
                    if (aSort.jointPriority === null && bSort.jointPriority === null) {
                        return 0;
                    }
                    if (aSort.jointPriority === null) {
                        return 1;
                    }
                    if (bSort.jointPriority === null) {
                        return -1;
                    }
                    return aSort.jointPriority - bSort.jointPriority;
                } else {
                    if (bSort.worstCase === aSort.worstCase) {
                        if (aSort.proScore && bSort.proScore) {
                            return aSort.proScore - bSort.proScore;
                        }

                        if (aSort.proScore && !bSort.proScore) {
                            return -1
                        }

                        if (!aSort.proScore && bSort.proScore) {
                            return 1
                        }

                        return 0
                    }

                    return bSort.worstCase - aSort.worstCase;
                }
                return bSort.worstCase - aSort.worstCase;
            })
            .map(([fullJoint]) => (
                <TriageJointOutcomesSection
                    requiredViews={appointmentRequiredJointViews![fullJoint as FullJoints]}
                    key={fullJoint}
                    appointment={appointment!}
                    hideCTA={!user?.can(UPDATE$XRAY)}
                    roiImgUrls={roiImgUrls}
                    roiImagesLoading={roiImagesLoading}
                    fullJoint={fullJoint as FullJoints}
                    pro={latestProsMappedToJoints[fullJoint as FullJoints]!}
                    xrays={xraysMappedToJoints[fullJoint as FullJoints]!}
                    displayVertical={mdBreached}
                    allXraysConfirmed={
                        !filterAccionPredSectsByFullJoint(
                            xraysMappedToJoints[fullJoint as FullJoints]?.map((xray) => xray.prediction_results.sections).flat() || [],
                            fullJoint as FullJoints,
                        ).find((sectionPrediction) => !sectionPrediction.clientConfirmed)
                    }
                    handleConfirmXrays={() => batchConfirmXrays(xraysMappedToJoints[fullJoint as FullJoints] || [], fullJoint as FullJoints)}
                    confirmRequestLoading={updateXrayLoading}
                    jointDegenOutcomes={
                        mapClientFavoredPredictionsFromAccionPredSects(
                            filterAccionPredSectsByFullJoint(
                                xraysMappedToJoints[fullJoint as FullJoints]?.map((xray) => xray.prediction_results.sections).flat() || [],
                                fullJoint as FullJoints,
                            )
                        )
                    }
                    thumbnailXray={getThumbnailXray(fullJoint as FullJoints, xraysMappedToJoints[fullJoint as FullJoints] || [])}
                />
            ));

    return (<>
        {!props.initalQueriesLoading && !jointCards.length && (
            <MDBox {...containerStyles({
                textAlign: 'center',
                padding: '5% 10%',
                paddingBottom: '2rem',
                width: '100%',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
            })}>
                <MDTypography
                    variant='h3'
                    sx={{
                        color: colors.grey[600],
                        marginBottom: '2rem',
                    }}
                >
                    Fully analyzed joint outcomes will appear here.
                </MDTypography>
                <MDTypography
                    variant='body1'
                    sx={{
                        color: colors.grey[600]
                    }}
                >
                    Missing a joint?
                    <br />
                    You may still need to provide PROs or required X-rays.
                </MDTypography>
            </MDBox>
        )}

        <MDBox display="grid" marginBottom="2rem">

            {lgBreached && <MDBox display={lgBreached ? 'grid' : 'flex'} padding='2rem 0' gap="1rem">
                <MDButton
                    variant="outlined"
                    fullWidth={!lgBreached}
                    style={{
                        left: '0px',
                        color: colors.secondary.main,
                        borderColor: colors.secondary.main
                    }}
                    onClick={() => navigate(flowBackRoute)}>
                    <FontAwesomeIcon
                        icon={faChevronLeft}
                        color={colors.secondary.main}
                        style={{ marginRight: '.5rem' }} />
                    {flow === "TRIAGE" ? "Triage List" : "Appointment"}
                </MDButton>
                <MDButton
                    fullWidth={lgBreached}
                    variant={allXraysConfirmed ? "contained" : "outlined"}
                    style={{
                        width: lgBreached ? 'auto' : 'fit-content',
                        color: allXraysConfirmed ? colors.white.main : colors.secondary.main,
                        backgroundColor: allXraysConfirmed ? colors.secondary.main : colors.white.main,
                        borderColor: colors.secondary.main
                    }}
                    onClick={() => allXraysConfirmed
                        ? navigateToBaseRouteWithAptmtDetails({
                            routeKey: reportRoute,
                            appointmentId: appointment!.appointment_id,
                            appointmentDateISOString: appointment!.appointment_date,
                            addtlQueryParams: flow === "DEMO" ? { patientName: (`${activePatient?.first_name}_${activePatient?.last_name}`).toLowerCase() } : {},
                        })
                        : batchConfirmXrays(allActiveJointXRays || [])}>
                    {allXraysConfirmed ? 'View Summary Report' : 'Confirm All Joints'}
                </MDButton>
            </MDBox>}

            <MDBox
                {...containerStyles({
                    width: '100%',
                    textAlign: lgBreached ? 'center' : 'left',
                    marginBottom: '2rem',
                    alignItems: 'center',
                    borderRadius: '8px',
                    padding: '.5rem 0',
                    background: `linear-gradient(90deg, rgba(255,255,255,0) 0%, ${colors.disabled.focus} ${mdBreached ? '20%' : '40%'}, ${colors.disabled.focus} 50%, ${colors.disabled.focus} ${mdBreached ? '80%' : '60%'}, rgba(255,255,255,0) 100%)`,
                    position: 'relative',
                    display: 'grid',
                    justifyContent: 'space-around',
                    gridTemplateColumns: `${lgBreached ? '' : 'auto 1fr auto'}`,
                })}
            >
                {!lgBreached &&
                    <MDButton
                        variant="outlined"
                        style={{
                            width: 'fit-content',
                            color: colors.secondary.main, borderColor: colors.secondary.main
                        }}
                        onClick={() => navigate(flowBackRoute)}
                        disabled={flow === "DEMO" ? true : false}>

                        <FontAwesomeIcon
                            icon={faChevronLeft}
                            color={colors.secondary.main}
                            style={{ marginRight: '.5rem' }} />
                        {flow === "TRIAGE" ? "Triage List" : "Patient List"}
                    </MDButton>
                }

                <MDTypography
                    textAlign="center"
                    sx={{ color: colors.secondary.main }}
                    variant='h3'
                    fontWeight='regular'>
                    X-RAY ANALYSIS OUTCOMES
                </MDTypography>

                {!lgBreached &&
                    <MDButton
                        variant={allXraysConfirmed ? "contained" : "outlined"}
                        style={{
                            width: 'fit-content',
                            color: allXraysConfirmed ? colors.white.main : colors.secondary.main,
                            backgroundColor: allXraysConfirmed ? colors.secondary.main : colors.white.main,
                            borderColor: colors.secondary.main
                        }}
                        onClick={() => allXraysConfirmed
                            ? navigateToBaseRouteWithAptmtDetails({
                                routeKey: reportRoute,
                                appointmentId: appointment!.appointment_id,
                                appointmentDateISOString: appointment!.appointment_date,
                                addtlQueryParams: flow === "DEMO" ? { patientName: (`${activePatient?.first_name}_${activePatient?.last_name}`).toLowerCase() } : {},
                            })
                            : batchConfirmXrays(allActiveJointXRays || [])}>
                        {allXraysConfirmed ? 'View Summary Report' : 'Confirm All Joints'}
                    </MDButton>
                }
            </MDBox>
        </MDBox>

        <Container>
            {jointCards}
        </Container>

        {
            !!jointCards.length
            && jointCards.length !== Object.values(appointment?.joints || []).filter((rank) => !!rank).length
            && (
                <MDTypography
                    variant='body2'
                    sx={{
                        color: colors.grey[600],
                        width: '100%',
                        textAlign: 'center',
                        margin: '2.5rem 0 1.5rem 0',
                    }}
                >
                    Missing a joint? You may still need to provide a required X-ray.
                </MDTypography>
            )
        }

    </>)

}